import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { VngDialogRef, VNG_DIALOG_DATA } from "@vengreso/angular-components";
import { FormGroup, FormBuilder } from "@angular/forms";

import { DataService } from 'src/app/core/core-services/services/dataservices/data.service';
import { NotificationService } from 'src/app/core/core-services/services/notification.service';
import { ValidationConstants } from "src/app/core/core-services/constants/validation.constants";

import { AuthService } from 'src/app/core/core-services/services/auth.service';
import { DialogService } from 'src/app/core/core-services/services/dialog.service';
import { DOCUMENT, Location } from '@angular/common';
import { ProfileUpdatedSuccessfullyComponent } from 'src/app/components/dialogs/profile-updated-successfully/profile-updated-successfully.component';
import { Store } from '@ngrx/store';
import { selectPlanData } from 'src/app/core/store/plan/plan.selectors';

@Component({
    selector: 'app-fly-cut-invite',
    templateUrl: './fly-cut-invite.component.html',
    styleUrls: ['./fly-cut-invite.component.scss'],
    standalone: false
})
export class FlyCutInviteComponent implements OnInit {

  inviteCounter = 0;
  firstPersonSignedUp: any = false;
  secondPersonSignedUp: any = false;
  thirdPersonSignedUp: any = false;
  fourthPersonSignedUp: any = false;

  flyshareInviteLink: string;

  firstName:string;
  lastName:string;

  form: FormGroup;
  emails: string[] = [];

  timeout: any = null;

  @ViewChild('emailInput', { static: true }) emailInput!: ElementRef<HTMLInputElement>;

  // selectPlanData, not `state => state.data`: the root state is { plan: PlanState },
  // so `state.data` is always undefined and the old destructuring subscriber threw.
  currentPlan$ = this.store.select(selectPlanData);

  constructor(
    @Inject(VNG_DIALOG_DATA) public data : any,
    public dialog: DialogService,
    public dialogRef: VngDialogRef<FlyCutInviteComponent>,
    private readonly dataService: DataService,
    private readonly notification: NotificationService,
    private readonly fb: FormBuilder,
    public authService: AuthService,
    private readonly cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: any,
    private readonly location: Location,
    private readonly store: Store,
  ) {

    let user = this.authService.userDetails;
    this.flyshareInviteLink = `${this.document.location.origin}/session/signup?referel=${user?.user_details?.referral_key||''}`
    const {first_name, last_name} = this.authService.userDetails.user_details;

    this.firstName = first_name;
    this.lastName = last_name;

    this.form = this.fb.group({
      first_name: [
        first_name,
        [
          Validators.required,
          Validators.pattern(ValidationConstants.NON_DIGIT),
          Validators.maxLength(12)
        ],
      ],
      last_name: [
        last_name,
        [
          Validators.required,
          Validators.pattern(ValidationConstants.NON_DIGIT),
          Validators.maxLength(12)
        ],
      ],
      message: ['']
    });
  }

  ngOnInit(): void {
    this.currentPlan$
    .subscribe((currentPlan) => {
         if (currentPlan?.referrals) {
           const referrals = currentPlan.referrals;
           if (referrals[0]) {
             this.inviteCounter = 1
             this.firstPersonSignedUp = JSON.parse(referrals[0])
           }
           if (referrals[1]) {
             this.inviteCounter = 2
             this.secondPersonSignedUp = JSON.parse(referrals[1])
           }
           if (referrals[2]) {
             this.inviteCounter = 3
             this.thirdPersonSignedUp = JSON.parse(referrals[2])
           }
           if (referrals[3]) {
             this.inviteCounter = 4
             this.fourthPersonSignedUp = JSON.parse(referrals[3])
           }
           this.cdr.detectChanges();
         }
       }
    );

    this.location.replaceState('rewards');
    // R3: markAllAsTouched() used to run here, which made the dialog open with
    // the name fields already painted red whenever the profile-prefilled value
    // failed validation. It moved to share() (the submit path) instead.
  }

  ngOnDestroy(): void {
    if(this.data?.parentPage === 'subscription'){
      this.location.replaceState(`settings?tab=subscription`);
      return
    }
    this.location.replaceState(`flyboard`);
  }

  // Replaces the old matChipInputTokenEnd handler (04 §3.2 composition): reads
  // the native input's current value directly instead of a MatChipInputEvent.
  // Splits on comma/semicolon/whitespace so a value that still holds separators
  // (typed fast, or committed on blur) never becomes a single bogus chip.
  addFromInput(): void {
    const parts = this.splitEmails(this.emailInput.nativeElement.value || '');

    if (parts.length) {
      this.emails.push(...parts);
    }

    // Clear the input value
    this.emailInput.nativeElement.value = '';
  }

  // Material's matChipInputSeparatorKeyCodes = [ENTER, COMMA] used to tokenize
  // on comma. Angular has no `keydown.comma` pseudo-event (its key map only
  // aliases a handful of names), so the comma has to be caught on the raw
  // keydown — enter keeps its own `keydown.enter` binding in the template.
  onKeyDown(event: KeyboardEvent): void {
    if (event.key !== ',' && event.key !== ';') {
      return;
    }

    event.preventDefault();
    // Drop the pending onKeyUp auto-add: the chip is being committed now.
    clearTimeout(this.timeout);
    this.addFromInput();
  }

  // Splits pasted text on comma/space/newline so multiple emails paste in as
  // separate chips (04 §3.2 composition). A single-value paste (no separator
  // found) is left alone so the browser's default paste still fills the input.
  onPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text');

    if (!pastedText) {
      return;
    }

    const parts = this.splitEmails(pastedText);

    if (parts.length > 1) {
      event.preventDefault();
      parts.forEach((email) => this.emails.push(email));
      this.emailInput.nativeElement.value = '';
    }
  }

  // Splits a raw string on comma / semicolon / whitespace and trims the pieces.
  private splitEmails(raw: string): string[] {
    return raw
      .split(/[,;\s]+/)
      .map((email) => email.trim())
      .filter((email) => !!email);
  }

  onKeyUp(event: any) {
    clearTimeout(this.timeout);
    let that = this;

    // The separator keys already committed the chip in onKeyDown/keydown.enter.
    if (event.key === ',' || event.key === ';' || event.key === 'Enter') {
      return;
    }

    this.timeout = setTimeout(function () {
      if (event.key !== 'Enter') {
        const email = (event.target.value || '').trim();

        const emailPatternValidator = new FormControl(email.toLowerCase(), [
          Validators.pattern(ValidationConstants.EMAIL_EXTRA_REGEX),
          Validators.minLength(5),
          Validators.email,
          Validators.required
        ]);

        if(emailPatternValidator.valid) {
          event.target.value = ''
          that.emails.push(email)
        }
      }
    }, 1000);
  }

  remove(email: any): void {
    const i = this.emails.indexOf(email);

    if (i >= 0) {
      this.emails.splice(i, 1);
    }
  }

  close(): void {
    this.dialogRef.close({modal: 'flycut_invite'});
  }

  // The shared app-copy-link-field owns the clipboard copy + tooltip; this
  // host-side (copied) handler keeps the success toast.
  onLinkCopied(): void {
    this.notification.toast("Link copied successfully!");
  }

  share(): void {
    this.form.markAllAsTouched();

    if(this.invalidEmail()){
      this.notification.toast('Invalid email(s)', 'Ok', 2000);
      return;
    }

    if (!this.form.valid) {
      this.notification.toast('Form error', 'Ok', 2000);
      return;
    }

    const { value : {first_name, last_name, message} } = this.form;

    const data = {
      first_name,
      last_name,
      invited_email: this.emails.map(email => email.trim()).join(','),
      personal_message: message
    }
    
    this.dataService.post({url: 'v1/user/invitation', data: data, isLoader: true}).subscribe({
      next: (response:any) => {
        this.notification.toast('Invitation sent successfully', 'Ok', 2000);

        if(this.nameChanged(data)) {
          this.promptUpdateProfile();
        }

        // this.form.reset();
        this.emails = [];
        this.form.controls['message'].reset()
      },
      error: (error) => {
        this.notification.toast(error.error.error ?? 'Something Went Wrong. Check fields.', 'Ok', 2000)
      }
    });
  }

  invalidEmail() {
    return this.emails.some(email => 
       (new FormControl(email.toLowerCase(), [
        Validators.pattern(ValidationConstants.EMAIL_EXTRA_REGEX),
        Validators.minLength(5),
        Validators.email,
        Validators.required
      ])).invalid
    );
  }
  
  nameChanged(payload: any): boolean {
    return (this.firstName !== payload.first_name || this.lastName !== payload.last_name);
  }

  promptUpdateProfile(): void {
    this.dialog
      .openDialogComponent<boolean>(ProfileUpdatedSuccessfullyComponent, { formValue: this.form.value }, "450px")
      .afterClosed()
      .subscribe((confirmed) => confirmed && this.updateProfile());
  }

  updateProfile() {
    let data = this.form.value
    this.dataService
      .put({ url: "v1/user", isLoader: false, data })
      .subscribe((response: any) => {
        if (response && response.result) {
          this.authService.updateUserDetail();
        }
      },
      (error) => {},
      () => {});
  }

}
